import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { validateSession, refreshSession } from "@/services/auth.service";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useSessionWarning } from "@/hooks/useSessionWarning";
import SessionWarningDialog from "@/components/SessionWarningDialog";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [checking, setChecking] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);
  const navigate = useNavigate();
  const { refreshOrganizations } = useOrganization();

  const fetchSession = useCallback(async () => {
    const session = await refreshOrganizations();
    if (!session) {
      navigate("/", { replace: true });
      return;
    }
    if (session.sessionExpiresAt) {
      setSessionExpiresAt(session.sessionExpiresAt);
    }
    setChecking(false);
  }, [navigate, refreshOrganizations]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const onSilentRefresh = useCallback(async () => {
    const ok = await refreshSession();
    if (!ok) return;
    const session = await validateSession();
    if (session?.sessionExpiresAt) {
      setSessionExpiresAt(session.sessionExpiresAt);
    }
  }, []);

  const { show, secondsLeft, handleStayLoggedIn, handleDismiss } = useSessionWarning(sessionExpiresAt, onSilentRefresh);

  const onStayLoggedIn = useCallback(async () => {
    await handleStayLoggedIn();
    // Re-fetch /auth/me to get the updated expiry from the new cookie
    const session = await validateSession();
    if (session?.sessionExpiresAt) {
      setSessionExpiresAt(session.sessionExpiresAt);
    }
  }, [handleStayLoggedIn]);

  if (checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {children}
      {show && (
        <SessionWarningDialog
          secondsLeft={secondsLeft}
          onStayLoggedIn={onStayLoggedIn}
        />
      )}
    </>
  );
};

export default ProtectedRoute;
